import { useEffect, useState, useMemo } from 'react';
import AdminLayout from "../../layouts/AdminLayout";
import {
  Box,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  Button,
  Flex,
  HStack
} from '@chakra-ui/react';
import adminService from '../../services/adminService';
import { formatDate } from '../../utils/helper';
import ReactPaginate from "react-paginate";
import { FaFileCsv, FaFilePdf } from "react-icons/fa";
import { exportToPDF } from '../../utils/pdfExporter';
import { exportToCSV } from '../../utils/csvExporter';

const ITEMS_PER_PAGE = 10;

const PaymentHistory = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    transactions();
  }, []);

  const transactions = async () => {
    let params = {
      authToken: user.authToken
    };
    setLoading(true);
    try {
      const res = await adminService.getPaymentHistory(params);
      if (res.statusCode === 200) {
        setData(res.data);
      }
    } catch (error) {
      alert(error.response?.data?.message || "Something went wrong.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let result = [...data];
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter((item) =>
      (`${item.FName} ${item.LName}`.toLowerCase().includes(lowerSearch) ||
        (item.Subscription || '').toLowerCase().includes(lowerSearch) ||
        (item.Stat).toLowerCase().includes(lowerSearch) ||
        String(item.MobileNo).includes(lowerSearch) ||
        String(item.billId).includes(lowerSearch) ||
        String(item.FrmDt).includes(lowerSearch) ||
        String(item.ToDt).includes(lowerSearch) ||
        String(item.CreatedDt).includes(lowerSearch) ||
        item.CompName.includes(lowerSearch) ||
        String(item.PaidAmt).includes(lowerSearch))
      );
    }
    if (sortField) {
      result.sort((a, b) => {
        let aVal = a[sortField] ?? '';
        let bVal = b[sortField] ?? '';
        if (sortField === "PaidAmt") {
          aVal = parseFloat(aVal);
          bVal = parseFloat(bVal);
        }
        return sortOrder === "asc" ? aVal > bVal ? 1 : -1 : aVal < bVal ? 1 : -1;
      });
    }
    return result;
  }, [data, search, sortField, sortOrder]);

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const currentData = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = ({ selected }) => {
    setPage(selected);
  };

  const exportToCSV1 = () => {
    // data, filename
    exportToCSV(filteredData, "payment_history.csv")
  };

  const exportToPDF1 = () => {
    const columns = [
      { label: "Bill Id", accessor: "billId" },
      {
        label: "Fullname",
        accessor: (item) =>
          `${item.FName} ${item.LName}`
      },
      { label: "MobileNo", accessor: "MobileNo" },
      { label: "Plan", accessor: "Subscription" },
      { label: "Amount", accessor: "PaidAmt" },
      {
        label: "Plan Date",
        accessor: (item) =>
          `${formatDate(item.FrmDt)} to ${formatDate(item.ToDt)}`
      },
      { label: "Payment Mode", accessor: "mode" },
      { label: "Status", accessor: "Stat" },
      { label: "OrderRef", accessor: "OrderRef" },
      { label: "PRef", accessor: "PRef" },
      { label: "Company", accessor: "CompName" },
      { label: "Date", accessor: `CreatedDt` },
    ];

    exportToPDF({
      data: filteredData,      // your actual data array
      columns,
      fileName: "payment_history.pdf"
    });
  };

  return (
    <AdminLayout>
      <Box maxW="1000px" mx="auto" px={4}>
        <Box
          bg="white"
          p={6}
          rounded="xl"
          boxShadow="md"
          border="1px solid"
          borderColor="gray.200"
        >

          <Flex justify="space-between" mb={4} flexWrap="wrap" gap={4}>
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              maxW="300px"
              size="sm"
            />
            <HStack>
              <Button size="sm" colorScheme="blue" leftIcon={<FaFileCsv />} onClick={exportToCSV1}>
                CSV
              </Button>
              <Button size="sm" colorScheme="red" leftIcon={<FaFilePdf />} onClick={exportToPDF1}>
                PDF
              </Button>
            </HStack>
          </Flex>

          {loading ? (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" />
              <Text mt={4}>Loading transactions...</Text>
            </Box>
          ) : (
            <>

              <TableContainer overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th cursor="pointer" onClick={() => handleSort("billId")}>BillId</Th>
                      <Th cursor="pointer" onClick={() => handleSort("FName")}>Name</Th>
                      <Th cursor="pointer" onClick={() => handleSort("MobileNo")}>MobileNo</Th>
                      <Th cursor="pointer" onClick={() => handleSort("Subscription")}>Plan</Th>
                      <Th cursor="pointer" onClick={() => handleSort("PaidAmt")}>Amount</Th>
                      <Th cursor="pointer" onClick={() => handleSort("FrmDt")}>Date</Th>
                      <Th cursor="pointer" onClick={() => handleSort("PMode")}>PMode</Th>
                      <Th cursor="pointer" onClick={() => handleSort("CompName")}>Company</Th>
                      <Th cursor="pointer" onClick={() => handleSort("Stat")}>Status</Th>
                      <Th cursor="pointer" onClick={() => handleSort("OrderRef")}>Order Ref</Th>
                      <Th cursor="pointer" onClick={() => handleSort("PymtRef")}>PRef</Th>
                      <Th cursor="pointer" onClick={() => handleSort("CreatedDt")}>Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentData.length > 0 ? currentData.map((user) => (
                      <Tr key={user.billId}>
                        <Td>{user.billId}</Td>
                        <Td>{user.FName} {user.LName}</Td>
                        <Td>{user.MobileNo}</Td>
                        <Td>{user.Subscription}</Td>
                        <Td>{user.PaidAmt}</Td>
                        <Td>{formatDate(user.FrmDt)} to {formatDate(user.ToDt)}</Td>
                        <Td>{user.PMode}</Td>
                        <Td>{user.CompName}</Td>
                        <Td>{user.Stat}</Td>
                        <Td>{user.OrderRef}</Td>
                        <Td>{user.PymtRef}</Td>
                        <Td>{formatDate(user.CreatedDt)}</Td>
                      </Tr>
                    )) : (
                      <Tr>
                        <Td colSpan={10}><Text textAlign="center">No data found</Text></Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>

              <Box mt={6}>
                <ReactPaginate
                  previousLabel="Previous"
                  nextLabel="Next"
                  pageCount={pageCount}
                  onPageChange={handlePageChange}
                  containerClassName="pagination"
                  activeClassName="active"
                  pageClassName="page-item"
                  previousClassName="page-item"
                  nextClassName="page-item"
                  breakClassName="page-item"
                  pageLinkClassName="page-link"
                  previousLinkClassName="page-link"
                  nextLinkClassName="page-link"
                  breakLinkClassName="page-link"
                />
              </Box>

              <style>{`
              .pagination {
                display: flex;
                justify-content: center;
                list-style: none;
                padding: 0;
                margin-top: 1rem;
              }
              .page-item {
                margin: 0 5px;
              }
              .page-link {
                padding: 8px 12px;
                border-radius: 6px;
                background-color: #e2e8f0;
                cursor: pointer;
              }
              .page-link:hover {
                background-color: #cbd5e0;
              }
              .active .page-link {
                background-color: #3182ce;
                color: white;
              }
            `}</style>
            </>
          )}
        </Box>
      </Box>
    </AdminLayout>
  );
};

export default PaymentHistory;
